/**
 * LabelTemplateEditor — admin layout editor. For each label type
 * (Prep / Retail) admins toggle which optional fields print and drag
 * their order (via up/down controls). Product name + site header always
 * print. Saved per-site; drives both the on-screen preview and the
 * printed TSPL.
 */
import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import {
  PREP_FIELDS, RETAIL_FIELDS, DEFAULT_PREP_TEMPLATE, DEFAULT_RETAIL_TEMPLATE,
} from "@/lib/labelFields";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Save, RotateCcw } from "lucide-react";

// Build ordered [{key,label,visible}] from a saved template: visible
// fields first (in saved order), then hidden fields appended.
function buildRows(allFields, template) {
  const tmpl = Array.isArray(template) && template.length ? template : allFields.map((f) => f.key);
  const byKey = Object.fromEntries(allFields.map((f) => [f.key, f]));
  const seen = new Set();
  const rows = [];
  tmpl.forEach((k) => {
    if (byKey[k] && !seen.has(k)) { rows.push({ ...byKey[k], visible: true }); seen.add(k); }
  });
  allFields.forEach((f) => {
    if (!seen.has(f.key)) rows.push({ ...f, visible: false });
  });
  return rows;
}

function FieldList({ title, rows, setRows }) {
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };
  const toggle = (i) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, visible: !r.visible } : r)));

  return (
    <div className="space-y-2">
      <div className="font-display font-bold text-base">{title}</div>
      <ul className="space-y-2">
        {rows.map((r, i) => (
          <li
            key={r.key}
            data-testid={`tmpl-row-${r.key}`}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${r.visible ? "bg-white" : "bg-neutral-50 opacity-70"}`}
            style={{ borderColor: "var(--border)" }}
          >
            <Checkbox
              data-testid={`tmpl-toggle-${r.key}`}
              checked={r.visible}
              onCheckedChange={() => toggle(i)}
            />
            <span className="flex-1 text-sm font-medium">{r.label}</span>
            <div className="flex gap-1">
              <button data-testid={`tmpl-up-${r.key}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-1 disabled:opacity-30 hover:text-eucalyptus-700" aria-label="Move up">
                <ChevronUp className="w-4 h-4" />
              </button>
              <button data-testid={`tmpl-down-${r.key}`} onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="p-1 disabled:opacity-30 hover:text-eucalyptus-700" aria-label="Move down">
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function LabelTemplateEditor({ activeSite, refresh }) {
  const [prepRows, setPrepRows] = useState([]);
  const [retailRows, setRetailRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!activeSite) return;
    setPrepRows(buildRows(PREP_FIELDS, activeSite.prep_template));
    setRetailRows(buildRows(RETAIL_FIELDS, activeSite.retail_template));
  }, [activeSite]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/sites/${activeSite.id}`, {
        prep_template: prepRows.filter((r) => r.visible).map((r) => r.key),
        retail_template: retailRows.filter((r) => r.visible).map((r) => r.key),
      }, withSite(activeSite.id));
      if (refresh) await refresh();
      toast.success("Label templates saved");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setPrepRows(buildRows(PREP_FIELDS, DEFAULT_PREP_TEMPLATE));
    setRetailRows(buildRows(RETAIL_FIELDS, DEFAULT_RETAIL_TEMPLATE));
  };

  return (
    <div className="mt-4 space-y-6" data-testid="label-template-editor">
      <p className="text-sm text-charcoal-soft">
        Choose which fields print on 60×50mm labels and drag them into order.
        The product name and site header always print.
      </p>
      <div className="grid md:grid-cols-2 gap-8 surface p-6">
        <FieldList title="Kitchen Prep label" rows={prepRows} setRows={setPrepRows} />
        <FieldList title="Retail label" rows={retailRows} setRows={setRetailRows} />
      </div>
      <div className="flex justify-end gap-3">
        <Button data-testid="tmpl-reset" variant="ghost" className="gap-2" onClick={reset}>
          <RotateCcw className="w-4 h-4" /> Reset to defaults
        </Button>
        <Button data-testid="tmpl-save" disabled={saving} className="bg-ochre-600 hover:bg-ochre-400 text-white gap-2" onClick={save}>
          <Save className="w-4 h-4" /> {saving ? "Saving\u2026" : "Save templates"}
        </Button>
      </div>
    </div>
  );
}
